import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, User, ListChecks, CreditCard, LogOut, KeyRound, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import StartersForm from "@/components/StartersForm";
import PlanningMemorySettings from "@/components/PlanningMemorySettings";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/auth/AuthContext";
import { useLocalPref } from "@/hooks/useLocalPref";
import { EMPTY_STARTERS } from "@/lib/starters";
import { isUnlimited, planOf, roleOf, strategistPlanCredits, STRATEGIST_PLAN_POOL } from "@/lib/access";
import { openBillingPortal } from "@/lib/payments";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

export default function Settings() {
  const { t, locale } = useTranslation();
  const { user, updateProfile, changePassword, saveStarters, redeemCode, logout, formatApiError } = useAuth();

  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [secondName, setSecondName] = useState(user?.secondName || "");
  const [alias, setAlias] = useState(user?.alias || "");
  const [recoveryEmail, setRecoveryEmail] = useState(user?.recoveryEmail || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [savingStarters, setSavingStarters] = useState(false);

  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [showProgressPct, setShowProgressPct] = useLocalPref("chronicles.pref.showProgressPct", false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const manageBilling = async () => {
    setOpeningPortal(true);
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setOpeningPortal(false);
    }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      await redeemCode(code.trim());
      setCode("");
      toast.success(t("settings.codeApplied"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRedeeming(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await updateProfile({ firstName: firstName.trim(), secondName: secondName.trim(), alias: alias.trim(), recoveryEmail: recoveryEmail.trim() });
      toast.success(t("settings.accountSaved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    setSavingPw(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword(""); setNewPassword("");
      toast.success(t("settings.passwordChanged"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingPw(false);
    }
  };

  const onSaveStarters = async (value) => {
    setSavingStarters(true);
    try {
      await saveStarters(value);
      toast.success(t("starters.saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingStarters(false);
    }
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-2xl px-4 py-3 flex items-center gap-2">
          <Link to="/" className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="settings-back" aria-label={t("settings.backToHome")}><ArrowLeft className="h-4 w-4" /></Link>
          <div className="flex-1"><p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Chronicles</p><p className="font-display text-base font-semibold">{t("settings.title")}</p></div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-2xl px-4 py-6 space-y-8 pb-24">
        {/* Account */}
        <section data-testid="settings-account">
          <div className="flex items-center gap-2 mb-3"><User className="h-4 w-4 text-slate-700" /><h2 className="font-display text-lg font-semibold">{t("settings.account")}</h2></div>
          <div className="rounded-2xl border border-border bg-card p-4 space-y-4">
            <Field label={t("auth.firstName")}><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="rounded-xl h-12" data-testid="settings-firstName" /></Field>
            <Field label={t("auth.secondName")}><Input value={secondName} onChange={(e) => setSecondName(e.target.value)} className="rounded-xl h-12" data-testid="settings-secondName" /></Field>
            <Field label={t("auth.alias")} hint={t("auth.aliasHint")}><Input value={alias} onChange={(e) => setAlias(e.target.value)} className="rounded-xl h-12" data-testid="settings-alias" /></Field>
            <Field label={t("auth.recoveryEmail")} hint={t("auth.recoveryEmailHint")}><Input type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} className="rounded-xl h-12" data-testid="settings-recoveryEmail" /></Field>
            <Button className="rounded-full h-11 bg-slate-900 hover:bg-slate-800" onClick={saveProfile} disabled={savingProfile} data-testid="settings-save-account">{t("settings.saveAccount")}</Button>
          </div>
        </section>

        {/* Password */}
        <section data-testid="settings-password">
          <div className="flex items-center gap-2 mb-3"><KeyRound className="h-4 w-4 text-slate-700" /><h2 className="font-display text-lg font-semibold">{t("settings.changePassword")}</h2></div>
          <div className="rounded-2xl border border-border bg-card p-4 space-y-4">
            <Field label={t("settings.currentPassword")}><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="rounded-xl h-12" data-testid="settings-current-password" /></Field>
            <Field label={t("settings.newPassword")}><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="rounded-xl h-12" data-testid="settings-new-password" /></Field>
            <Button className="rounded-full h-11 bg-slate-900 hover:bg-slate-800" onClick={savePassword} disabled={savingPw || !currentPassword || !newPassword} data-testid="settings-save-password">{t("settings.changePassword")}</Button>
          </div>
        </section>

        {/* Subscription */}
        <section data-testid="settings-subscription">
          <div className="flex items-center gap-2 mb-3"><CreditCard className="h-4 w-4 text-slate-700" /><h2 className="font-display text-lg font-semibold">{t("settings.subscription")}</h2></div>
          <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{t("settings.plan")}</span>
              <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-800 text-xs font-medium px-3 py-1" data-testid="settings-plan">
                {t(`settings.plans.${planOf(user)}`)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{t("settings.role")}</span>
              <span className="inline-flex items-center rounded-full bg-slate-900 text-white text-xs font-medium px-3 py-1" data-testid="settings-role">
                {t(`settings.roles.${roleOf(user)}`)}
              </span>
            </div>

            {user?.subscription?.activatedBy === "stripe" && planOf(user) !== "service" ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t("settings.status")}</span>
                  <span
                    className={[
                      "inline-flex items-center rounded-full text-xs font-medium px-3 py-1",
                      user?.subscription?.status === "past_due"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-emerald-100 text-emerald-800",
                    ].join(" ")}
                    data-testid="settings-sub-status"
                  >
                    {t(`settings.statuses.${user?.subscription?.status || "active"}`)}
                  </span>
                </div>
                {user?.subscription?.interval ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t("settings.billingInterval")}</span>
                    <span className="text-sm text-slate-800" data-testid="settings-sub-interval">
                      {t(`settings.intervals.${user.subscription.interval}`)}
                    </span>
                  </div>
                ) : null}
                {user?.subscription?.status === "past_due" ? (
                  <p className="text-[12px] text-amber-700 bg-amber-50 rounded-xl px-3 py-2 leading-relaxed" data-testid="settings-sub-pastdue">
                    {t("settings.pastDueMsg")}
                  </p>
                ) : user?.subscription?.cancelAtPeriodEnd && user?.subscription?.currentPeriodEnd ? (
                  <p className="text-[12px] text-slate-600 bg-slate-50 rounded-xl px-3 py-2" data-testid="settings-sub-ends">
                    {t("settings.endsOn", { date: new Date(user.subscription.currentPeriodEnd).toLocaleDateString(locale) })}
                  </p>
                ) : user?.subscription?.currentPeriodEnd && user?.subscription?.interval !== "trial" ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t("settings.renewsOn")}</span>
                    <span className="text-sm text-slate-800" data-testid="settings-sub-renews">
                      {new Date(user.subscription.currentPeriodEnd).toLocaleDateString(locale)}
                    </span>
                  </div>
                ) : null}
                <Button
                  variant="outline"
                  className="rounded-full h-11 w-full border-slate-300 text-slate-800 mt-1"
                  onClick={manageBilling}
                  disabled={openingPortal}
                  data-testid="settings-manage-subscription"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {t("settings.manageSubscription")}
                </Button>
              </>
            ) : null}

            {isUnlimited(user) ? (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{t("settings.aiCredits")}</span>
                <span className="text-sm font-medium text-emerald-700" data-testid="settings-credits-unlimited">{t("settings.unlimited")}</span>
              </div>
            ) : planOf(user) === "assistant" ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t("settings.assistantCredits")}</span>
                  <span className="text-sm text-slate-800 font-mono-tab" data-testid="settings-assistant-credits">
                    {user?.subscription?.assistantCreditsRemaining ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t("settings.strategistCredits")}</span>
                  <span className="text-sm text-slate-800 font-mono-tab" data-testid="settings-strategist-credits">
                    {user?.subscription?.strategistCreditsRemaining ?? 0}
                  </span>
                </div>
                {user?.subscription?.nextResetDate ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t("settings.nextReset")}</span>
                    <span className="text-sm text-slate-800" data-testid="settings-next-reset">
                      {new Date(user.subscription.nextResetDate).toLocaleDateString(locale)}
                    </span>
                  </div>
                ) : null}
              </>
            ) : planOf(user) === "strategist" ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t("settings.aiCredits")}</span>
                  <span className="text-sm text-slate-800 font-mono-tab" data-testid="settings-strategist-plan-credits">
                    {strategistPlanCredits(user)} / {STRATEGIST_PLAN_POOL}
                  </span>
                </div>
                {user?.subscription?.nextResetDate ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">{t("settings.nextReset")}</span>
                    <span className="text-sm text-slate-800" data-testid="settings-next-reset-strategist">
                      {new Date(user.subscription.nextResetDate).toLocaleDateString(locale)}
                    </span>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-[12px] text-slate-500 leading-relaxed" data-testid="settings-upgrade-hint">{t("settings.upgradeHint")}</p>
            )}

            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">{t("settings.lastLogin")}</span>
              <span className="text-sm text-slate-800 font-mono-tab" data-testid="settings-last-login">
                {user?.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString(locale) : "—"}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">{t("settings.inactivityWarning")}</p>

            {/* Redeem code */}
            <div className="pt-3 border-t border-border">
              <Field label={t("settings.redeemCode")} hint={t("settings.redeemHint")}>
                <div className="flex gap-2">
                  <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ASSIST-XXXXXXXX" className="rounded-xl h-12 font-mono-tab flex-1" data-testid="settings-code-input" />
                  <Button className="rounded-xl h-12 px-5 bg-slate-900 hover:bg-slate-800 shrink-0" onClick={redeem} disabled={redeeming || !code.trim()} data-testid="settings-redeem-code">
                    {t("settings.redeem")}
                  </Button>
                </div>
              </Field>
            </div>
          </div>
        </section>

        {/* Starters */}
        <section data-testid="settings-preferences">
          <div className="flex items-center gap-2 mb-3"><Percent className="h-4 w-4 text-slate-700" /><h2 className="font-display text-lg font-semibold">{t("settings.preferencesSection")}</h2></div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{t("settings.progressPctTitle")}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t("settings.progressPctHint")}</p>
              </div>
              <Switch checked={showProgressPct} onCheckedChange={setShowProgressPct} data-testid="settings-progress-pct-toggle" />
            </div>
          </div>
        </section>

        {/* Starters */}
        <section data-testid="settings-starters">
          <div className="flex items-center gap-2 mb-3"><ListChecks className="h-4 w-4 text-slate-700" /><h2 className="font-display text-lg font-semibold">{t("settings.startersSection")}</h2></div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <StartersForm
              initial={user?.starters || EMPTY_STARTERS}
              onSubmit={onSaveStarters}
              submitLabel={t("starters.save")}
              busy={savingStarters}
            />
          </div>
        </section>

        {/* Planning memory (AI) */}
        <PlanningMemorySettings />

        <Button variant="outline" className="rounded-full h-11 w-full border-slate-300 text-slate-700" onClick={logout} data-testid="settings-logout">
          <LogOut className="h-4 w-4 mr-2" /> {t("settings.logout")}
        </Button>
      </main>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">{label}</Label>
      {children}
      {hint ? <p className="text-[11px] text-slate-400">{hint}</p> : null}
    </div>
  );
}
